'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useBooking } from '../../context/BookingContext';
import { type StepNav, useRegisterStepNav } from '../../context/StepNavContext';
import type { AddressAutocompletePrediction } from '../../types';

const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_MS = 300;
const INPUT_ID = 'bt-zone-address-input';
const ERROR_ID = 'bt-zone-address-error';

interface PredictionDescriptionParts {
  primary: string;
  secondary: string | undefined;
}

function splitPredictionDescription(description: string): PredictionDescriptionParts {
  const [primary = description, ...secondaryParts] = description
    .split(',')
    .map((part): string => part.trim())
    .filter(Boolean);

  return {
    primary,
    secondary: secondaryParts.length > 0 ? secondaryParts.join(', ') : undefined,
  };
}

export function ZoneAddressStep() {
  const { ctx, send, labels, callbacks, goBack } = useBooking();

  /* Étape à auto-avance (la sélection d'une adresse valide confirme) : pas d'action principale. */
  useRegisterStepNav(useMemo((): StepNav => ({ onBack: goBack }), [goBack]));

  const [query, setQuery] = useState<string>('');
  const [predictions, setPredictions] = useState<AddressAutocompletePrediction[]>([]);
  const [loadingPredictions, setLoadingPredictions] = useState<boolean>(false);
  const [validating, setValidating] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const selectedPredictionQueryRef = useRef<string | undefined>(undefined);
  const autocompleteRequestIdRef = useRef<number>(0);

  useEffect((): (() => void) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmedQuery = query.trim();
    if (!callbacks.onAutocompleteAddress) return (): void => undefined;
    if (validating || trimmedQuery.length < MIN_QUERY_LENGTH || selectedPredictionQueryRef.current === trimmedQuery) {
      setPredictions([]);
      setLoadingPredictions(false);
      return (): void => undefined;
    }

    setLoadingPredictions(true);
    const onAutocomplete = callbacks.onAutocompleteAddress;
    const requestId = autocompleteRequestIdRef.current + 1;
    autocompleteRequestIdRef.current = requestId;

    debounceRef.current = setTimeout((): void => {
      onAutocomplete(trimmedQuery)
        .then((res): void => {
          if (autocompleteRequestIdRef.current !== requestId || selectedPredictionQueryRef.current) return;
          setPredictions(res);
          setLoadingPredictions(false);
        })
        .catch((): void => {
          if (autocompleteRequestIdRef.current !== requestId) return;
          setPredictions([]);
          setLoadingPredictions(false);
        });
    }, DEBOUNCE_MS);

    return (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, callbacks.onAutocompleteAddress, validating]);

  const selectPrediction = useCallback(
    (prediction: AddressAutocompletePrediction): void => {
      if (!callbacks.onValidateZoneAddress || ctx.selectedPlaceId == null) return;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      selectedPredictionQueryRef.current = prediction.description.trim();
      autocompleteRequestIdRef.current += 1;
      setQuery(prediction.description);
      setPredictions([]);
      setLoadingPredictions(false);
      setError(undefined);
      setValidating(true);
      callbacks
        .onValidateZoneAddress(ctx.selectedPlaceId, prediction.place_id)
        .then((res): void => {
          setValidating(false);
          send({ type: 'SAVE_CUSTOMER_ADDRESS', address: res.address });
        })
        .catch((caughtError: { errorKey?: string; message?: string }): void => {
          setValidating(false);
          const key = caughtError?.errorKey ?? '';
          if (key === 'ADDRESS_OUTSIDE_ZONE') {
            setError(labels.zone_address_outside_error);
          } else {
            setError(caughtError?.message ?? labels.error_generic);
          }
        });
    },
    [callbacks, ctx.selectedPlaceId, send, labels],
  );

  return (
    <div className="bt-step">
      <div className="bt-step-header">
        <h2 className="bt-step-header__title">{labels.zone_address_title}</h2>
        <p className="bt-step-header__subtitle">{labels.zone_address_subtitle}</p>
      </div>

      <div className="bt-zone-address">
        <div className="bt-zone-address__field">
          <input
            id={INPUT_ID}
            type="text"
            name="zone-address-search"
            className={`bt-zone-address__input ${error ? 'bt-zone-address__input--error' : ''}`}
            placeholder={labels.zone_address_placeholder}
            value={query}
            onChange={(event): void => {
              selectedPredictionQueryRef.current = undefined;
              autocompleteRequestIdRef.current += 1;
              setQuery(event.target.value);
              setError(undefined);
            }}
            disabled={validating}
            aria-invalid={!!error}
            aria-describedby={error ? ERROR_ID : undefined}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
          />

          {loadingPredictions && (
            <span className="bt-zone-address__input-spinner" role="status">
              <span className="bt-spinner bt-spinner--inline" />
            </span>
          )}
        </div>

        {query.trim().length > 0 && query.trim().length < MIN_QUERY_LENGTH && (
          <p className="bt-zone-address__hint">{labels.zone_address_search_hint}</p>
        )}

        {predictions.length > 0 && !validating && (
          <div className="bt-zone-address__panel">
            <ul className="bt-zone-address__list">
              {predictions.map((predictionItem): React.ReactElement => {
                const description = splitPredictionDescription(predictionItem.description);

                return (
                  <li key={predictionItem.place_id}>
                    <button
                      type="button"
                      className="bt-zone-address__item"
                      onClick={(): void => selectPrediction(predictionItem)}
                    >
                      <span className="bt-zone-address__item-main">{description.primary}</span>
                      {description.secondary && (
                        <span className="bt-zone-address__item-secondary">{description.secondary}</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {validating && (
          <div className="bt-zone-address__validating" role="status">
            <span className="bt-spinner bt-spinner--inline" />
          </div>
        )}

        {error && (
          <div id={ERROR_ID} className="bt-zone-address__error" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
